import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Package, Plus, Pencil, Trash2, X, Search, ChevronDown, ChevronUp, Loader as Loader2, CircleAlert as AlertCircle, CircleCheck as CheckCircle2, Image as ImageIcon, Folder, Barcode, Truck, BookOpen, Boxes, ShoppingCart, Layers, ArchiveX, PackageCheck, PackageX, Table2, FileSpreadsheet, FileDown } from 'lucide-react';
import { BulkEditProductsView } from './BulkEditProductsView';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { emitToast } from '@/components/ui/Toast';
import { supabase } from '../../lib/supabase';
import { useCompany } from '../../context/CompanyContext';
import { usePermissions } from '../../hooks/usePermissions';
import { InventoryMovementsView } from './InventoryMovementsView';
import { InventoryBalancesView } from './InventoryBalancesView';
import { ExternalWarehouseBalancesView } from './ExternalWarehouseBalancesView';
import { VariationWizard } from '../../components/inventory/VariationWizard';
import { fetchCompanySkuSettings, type CompanySkuSettings } from '../inventory/SkuSettingsTab';
import { fetchVatRate } from '../inventory/CompanySettingsView';
import { useTableSort, sortRows } from '@/hooks/useTableSort';
import { SortableTh } from '@/components/ui/SortableTh';

interface InventoryProduct {
  id: string;
  company_id: string;
  sku: string;
  name: string;
  price: number;
  cost: number | null;
  image_url: string | null;
  supplier_barcode: string | null;
  supplier_id: string | null;
  created_at: string;
  updated_at: string;
  group_ids: string[];
  group_names: string[];
  b2b_backorder_override: boolean | null;
  shopify_stock_status: 'in_stock' | 'out_of_stock' | null;
  shopify_status: 'active' | 'draft' | 'archived' | null;
  shopify_status_last_checked: string | null;
  is_active: boolean;
  description: string | null;
}

interface ProductFormPricelist {
  id: string;
  name: string;
  is_manual: boolean;
  calculated_price: number;
  resolved_discount_percent: number;
}

interface ProductGroup {
  id: string;
  parent_id: string | null;
  name: string;
}

interface ShopifySettings {
  pull_stock_status: boolean;
  alert_on_archived: boolean;
}

interface Supplier {
  id: string;
  name: string;
  supplier_number: string;
}

interface ProductImageRow {
  id: string;
  image_url: string;
  alt_text: string;
  position: number;
}

type ManualPriceMap = Record<string, string>;
type HubTab = 'catalog' | 'balances' | 'movements' | 'external_balances';

interface TabDef {
  id: HubTab;
  label: string;
  icon: React.ReactNode;
}

interface Toast {
  msg: string;
  ok: boolean;
}

interface ProductForm {
  sku: string;
  name: string;
  price: number;
  cost: number | null;
  group_ids: string[];
  image_url: string;
  supplier_barcode: string;
  supplier_id: string | null;
  manualPrices: ManualPriceMap;
  b2b_backorder_override: boolean | null;
  is_active: boolean;
  description: string;
  gallery_images: ProductImageRow[];
}

const MAX_GROUPS = 6;

const EMPTY_FORM: ProductForm = {
  sku: '',
  name: '',
  price: 0,
  cost: null,
  group_ids: [],
  image_url: '',
  supplier_barcode: '',
  supplier_id: null,
  manualPrices: {},
  b2b_backorder_override: null,
  is_active: true,
  description: '',
  gallery_images: [],
};

const HUB_TABS: TabDef[] = [
  { id: 'catalog', label: 'קטלוג מוצרים', icon: <BookOpen className="w-4 h-4" /> },
  { id: 'balances', label: 'יתרות מלאי', icon: <Boxes className="w-4 h-4" /> },
  { id: 'external_balances', label: 'מחסנים חיצוניים', icon: <ShoppingCart className="w-4 h-4" /> },
  { id: 'movements', label: 'תנועות מלאי', icon: <Layers className="w-4 h-4" /> },
];

function fmt(n: number): string {
  if (typeof n !== 'number' || isNaN(n)) return '0.00';
  return n.toFixed(2);
}

async function exportProductsToExcel(rows: InventoryProduct[], supplierMap: Record<string, string>) {
  const XLSX = await import('xlsx');
  const wsData = rows.map(r => ({
    'מק"ט': r.sku,
    'שם הפריט': r.name,
    'קבוצה': r.group_names.join(', '),
    'ספק': r.supplier_id ? (supplierMap[r.supplier_id] ?? '') : '',
    'מחיר צרכן': r.price,
    'עלות ספק': r.cost ?? '',
    'ברקוד ספק': r.supplier_barcode ?? '',
    'פעיל': r.is_active ? 'כן' : 'לא',
  }));
  const ws = XLSX.utils.json_to_sheet(wsData);
  ws['!cols'] = [{ wch: 14 }, { wch: 28 }, { wch: 18 }, { wch: 18 }, { wch: 12 }, { wch: 12 }, { wch: 16 }, { wch: 6 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'קטלוג מוצרים');
  XLSX.writeFile(wb, `קטלוג_מוצרים_${new Date().toISOString().slice(0, 10)}.xlsx`);
}

async function exportProductsToPDF(el: HTMLElement) {
  const { exportTableToPDF } = await import('../../lib/pdfExport');
  await exportTableToPDF(el, `קטלוג_מוצרים_${new Date().toISOString().slice(0, 10)}.pdf`, { orientation: 'landscape' });
}

function pathFromUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    const idx = pathname.indexOf('/storage/v1/object/public/');
    if (idx !== -1) {
      return pathname.substring(idx + 24); // length of '/storage/v1/object/public/'
    }
    return pathname;
  } catch {
    return url;
  }
}

async function deleteStorageObject(path: string): Promise<void> {
  if (!path) return;
  await supabase.storage.from('product-images').remove([path]);
}

function flattenGroups(
  groups: ProductGroup[],
  parentId: string | null = null,
  depth: number = 0
): ProductGroup[] {
  const result: ProductGroup[] = [];
  for (const group of groups) {
    if (group.parent_id === parentId) {
      result.push({
        ...group,
        name: '  '.repeat(depth) + group.name,
      });
      result.push(...flattenGroups(groups, group.id, depth + 1));
    }
  }
  return result;
}

function SmartSkuBuilder({
  prefix,
  onSeqSku,
  vatRate: _vatRate,
  priceInput,
  costInput,
  suppliers: _suppliers,
}: {
  prefix: string;
  onSeqSku: (sku: string) => void;
  vatRate: number;
  priceInput: number;
  costInput: number | null;
  suppliers: Supplier[];
}) {
  const [method, setMethod] = useState<'sequential' | 'formula'>('sequential');
  const [seq, setSeq] = useState('001');
  const [formula, setFormula] = useState('P');
  const [displaySku, setDisplaySku] = useState('');

  const generateSku = useCallback(() => {
    let result = '';
    if (method === 'sequential') {
      result = prefix + seq;
    } else {
      const p = fmt(priceInput);
      const c = fmt(costInput ?? 0);
      const markup = costInput ? fmt(priceInput - costInput) : fmt(priceInput);
      result = (formula.replace(/P/g, p).replace(/C/g, c).replace(/M/g, markup)) || prefix + seq;
    }
    setDisplaySku(result);
    return result;
  }, [method, seq, formula, prefix, priceInput, costInput]);

  useEffect(() => {
    const sku = generateSku();
    onSeqSku(sku);
  }, [generateSku, onSeqSku]);

  return (
    <div className="bg-gray-50 p-4 rounded border border-gray-200 space-y-3">
      <div className="text-sm font-medium">יצירת מקט חכמה</div>
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="radio"
            checked={method === 'sequential'}
            onChange={() => setMethod('sequential')}
          />
          סדרתי
        </label>
        {method === 'sequential' && (
          <div className="ml-4 space-y-1.5">
            <label className="text-xs">קידומת</label>
            <div className="text-xs text-gray-500">{prefix}</div>
            <label className="text-xs">סדר</label>
            <input
              type="text"
              value={seq}
              onChange={(e) => setSeq(e.target.value)}
              className="w-full px-2 py-1 border rounded text-sm"
              placeholder="001"
            />
          </div>
        )}
      </div>
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="radio"
            checked={method === 'formula'}
            onChange={() => setMethod('formula')}
          />
          נוסחה (P=מחיר, C=עלות, M=רווח)
        </label>
        {method === 'formula' && (
          <input
            type="text"
            value={formula}
            onChange={(e) => setFormula(e.target.value)}
            className="w-full ml-4 px-2 py-1 border rounded text-sm"
            placeholder="P"
          />
        )}
      </div>
      <div className="text-xs bg-white p-2 rounded border border-gray-200">
        תוצאה: <span className="font-mono font-semibold">{displaySku}</span>
      </div>
    </div>
  );
}

export function InventoryProductsPage() {
  const { activeCompanyId } = useCompany();
  const { can } = usePermissions();
  const canEdit = can({ module: 'inventory', level: 'editor' });

  const [products, setProducts] = useState<InventoryProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState<Toast | null>(null);
  const [search, setSearch] = useState('');

  const [skuSettings, setSkuSettings] = useState<CompanySkuSettings | null>(null);
  const [vatRate, setVatRate] = useState(17);
  const [formPricelists, setFormPricelists] = useState<ProductFormPricelist[]>([]);
  const [mlLoading, setMlLoading] = useState(false);

  const [groups, setGroups] = useState<ProductGroup[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [supplierFilter, setSupplierFilter] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<InventoryProduct | null>(null);
  const [form, setForm] = useState<ProductForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const [productType, setProductType] = useState<'regular' | 'variation'>('regular');
  const [wizardOpen, setWizardOpen] = useState(false);
  const [mlExpanded, setMlExpanded] = useState(false);

  const [skuManuallyOverridden, setSkuManuallyOverridden] = useState(false);
  const [_suggestedSeqSku, setSuggestedSeqSku] = useState('');        // eslint-disable-line @typescript-eslint/no-unused-vars
  const [skuPrefixResolved, setSkuPrefixResolved] = useState('');
  const [skuPrefixLoading, setSkuPrefixLoading] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<InventoryProduct | null>(null);
  const [deleteHasOperations, setDeleteHasOperations] = useState(false);
  const [deleteChecking, setDeleteChecking] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [shopifySettings, setShopifySettings] = useState<ShopifySettings | null>(null);
  const [_shopifyIntegrationId, setShopifyIntegrationId] = useState<string | null>(null); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [showArchivedAlert, setShowArchivedAlert] = useState(false);
  const [archivedCount, setArchivedCount] = useState(0);
  const [shopifyFilter, setShopifyFilter] = useState<'archived' | null>(null);
  const [showInactive, setShowInactive] = useState(false);

  const [activeTab, setActiveTab] = useState<HubTab>('catalog');
  const [bulkEditMode, setBulkEditMode] = useState(false);

  const fetchProducts = useCallback(async () => {
    if (!activeCompanyId) return;
    try {
      setLoading(true);
      const { data, error: err } = await supabase
        .from('inventory_products')
        .select(
          `id, company_id, sku, name, price, cost, image_url, description, supplier_barcode,
           supplier_id, created_at, updated_at, b2b_backorder_override, is_active,
           shopify_stock_status, shopify_status, shopify_status_last_checked,
           inventory_product_group_relations(group_id, inventory_product_groups(id, name))`
        )
        .eq('company_id', activeCompanyId);

      if (err) throw err;

      const products: InventoryProduct[] = (data || []).map((p: any) => ({
        id: p.id,
        company_id: p.company_id,
        sku: p.sku,
        name: p.name,
        price: p.price,
        cost: p.cost,
        image_url: p.image_url,
        description: p.description ?? null,
        supplier_barcode: p.supplier_barcode,
        supplier_id: p.supplier_id,
        created_at: p.created_at,
        updated_at: p.updated_at,
        group_ids: (p.inventory_product_group_relations || []).map((r: any) => r.group_id),
        group_names: (p.inventory_product_group_relations || []).map(
          (r: any) => r.inventory_product_groups?.name || ''
        ),
        b2b_backorder_override: p.b2b_backorder_override,
        shopify_stock_status: p.shopify_stock_status,
        shopify_status: p.shopify_status,
        shopify_status_last_checked: p.shopify_status_last_checked,
        is_active: p.is_active ?? true,
      }));

      setProducts(products);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Error loading products');
    } finally {
      setLoading(false);
    }
  }, [activeCompanyId]);

  const fetchSkuSettings = useCallback(async () => {
    if (!activeCompanyId) return;
    try {
      const settings = await fetchCompanySkuSettings(activeCompanyId);
      setSkuSettings(settings);
    } catch (err) {
      console.error('Error fetching SKU settings:', err);
    }
  }, [activeCompanyId]);

  const fetchVat = useCallback(async () => {
    if (!activeCompanyId) return;
    try {
      const rate = await fetchVatRate(activeCompanyId);
      setVatRate(rate);
    } catch (err) {
      console.error('Error fetching VAT:', err);
    }
  }, [activeCompanyId]);

  const fetchGroups = useCallback(async () => {
    if (!activeCompanyId) return;
    try {
      const { data, error: err } = await supabase
        .from('inventory_product_groups')
        .select('id, parent_id, name')
        .eq('company_id', activeCompanyId);
      if (err) throw err;
      setGroups(data || []);
    } catch (err) {
      console.error('Error fetching groups:', err);
    }
  }, [activeCompanyId]);

  const fetchSuppliers = useCallback(async () => {
    if (!activeCompanyId) return;
    try {
      const { data, error: err } = await supabase
        .from('inventory_suppliers')
        .select('id, name, supplier_number')
        .eq('company_id', activeCompanyId);
      if (err) throw err;
      setSuppliers(data || []);
    } catch (err) {
      console.error('Error fetching suppliers:', err);
    }
  }, [activeCompanyId]);

  const fetchAllPricelists = useCallback(async () => {
    if (!activeCompanyId) return;
    try {
      const { data, error: err } = await supabase
        .from('inventory_pricelists')
        .select('id, name, is_manual')
        .eq('company_id', activeCompanyId);
      if (err) throw err;
      const lists = (data || []).map((p: any) => ({
        id: p.id,
        name: p.name,
        is_manual: p.is_manual,
        calculated_price: 0,
        resolved_discount_percent: 0,
      }));
      setFormPricelists(lists);
    } catch (err) {
      console.error('Error fetching pricelists:', err);
    }
  }, [activeCompanyId]);

  const loadProductPricelists = useCallback(
    async (productId: string) => {
      if (!activeCompanyId) return;
      try {
        setMlLoading(true);
        const { data, error: err } = await supabase.rpc('get_product_calculated_prices', {
          p_product_id: productId,
        });
        if (err) throw err;
        const loaded = (data || []).map((row: any) => ({
          id: row.pricelist_id,
          name: row.pricelist_name,
          is_manual: row.is_manual,
          calculated_price: row.calculated_price || 0,
          resolved_discount_percent: row.resolved_discount_percent || 0,
        }));
        setFormPricelists(loaded);
      } catch (err) {
        console.error('Error loading product pricelists:', err);
      } finally {
        setMlLoading(false);
      }
    },
    [activeCompanyId]
  );

  const fetchShopifyIntegration = useCallback(async () => {
    if (!activeCompanyId) return;
    try {
      const { data, error: err } = await supabase
        .from('company_integrations')
        .select('id, settings')
        .eq('company_id', activeCompanyId)
        .eq('provider_name', 'shopify_manual')
        .single();

      if (err && err.code !== 'PGRST116') throw err;
      if (data) {
        setShopifyIntegrationId(data.id);
        const s = data.settings as any;
        const settings: ShopifySettings = {
          pull_stock_status: s?.pull_stock_status ?? false,
          alert_on_archived: s?.alert_on_archived ?? false,
        };
        setShopifySettings(settings);

        setShopifySettings(settings);
      }
    } catch (err) {
      console.error('Error fetching Shopify integration:', err);
    }
  }, [activeCompanyId]);

  useEffect(() => {
    fetchProducts();
    fetchGroups();
    fetchSuppliers();
    fetchSkuSettings();
    fetchAllPricelists();
    fetchVat();
    fetchShopifyIntegration();
  }, [
    fetchProducts,
    fetchGroups,
    fetchSuppliers,
    fetchSkuSettings,
    fetchAllPricelists,
    fetchVat,
    fetchShopifyIntegration,
  ]);

  useEffect(() => {
    if (shopifySettings?.alert_on_archived) {
      const archived = products.filter((p) => p.shopify_status === 'archived').length;
      setArchivedCount(archived);
      if (archived > 0) setShowArchivedAlert(true);
    }
  }, [products, shopifySettings]);

  const groupOptions = flattenGroups(groups);
  const showShopifyColumns = !!(
    shopifySettings &&
    (shopifySettings.pull_stock_status || shopifySettings.alert_on_archived)
  );

  const supplierMap = Object.fromEntries(suppliers.map((s) => [s.id, s.name]));
  const [exporting, setExporting] = useState(false);
  const catalogTableRef = useRef<HTMLDivElement>(null);
  const { sort, toggle } = useTableSort<string>('sku', 'asc');

  const filtered = (() => {
    const base = products.filter((p) => {
      if (!showInactive && !p.is_active) return false;
      if (shopifyFilter === 'archived' && p.shopify_status !== 'archived') return false;
      if (supplierFilter && p.supplier_id !== supplierFilter) return false;
      const q = search.toLowerCase();
      const supplierName = p.supplier_id ? (supplierMap[p.supplier_id] ?? '').toLowerCase() : '';
      return (
        p.name.toLowerCase().includes(q) ||
        p.sku.toLowerCase().includes(q) ||
        p.group_names.some((gn) => gn.toLowerCase().includes(q)) ||
        supplierName.includes(q)
      );
    });
    if (sort.key === 'group_names') {
      return sortRows(base.map(p => ({ ...p, _group: p.group_names.join(', ') })), '_group', sort.dir).map(({ _group, ...rest }) => rest as typeof base[number]);
    }
    if (sort.key === 'supplier_name') {
      return sortRows(base.map(p => ({ ...p, _sup: p.supplier_id ? (supplierMap[p.supplier_id] ?? '') : '' })), '_sup', sort.dir).map(({ _sup, ...rest }) => rest as typeof base[number]);
    }
    return sortRows(base, sort.key, sort.dir);
  })();

  const openAddModal = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setProductType('regular');
    setFormError('');
    setSkuManuallyOverridden(false);
    setSuggestedSeqSku('');
    setSkuPrefixResolved('');
    setModalOpen(true);
    setFormPricelists((prev) =>
      prev.map((p) => ({
        ...p,
        calculated_price: 0,
        resolved_discount_percent: 0,
      }))
    );
  };

  const openEditModal = async (product: InventoryProduct) => {
    setEditing(product);
    setForm({
      sku: product.sku,
      name: product.name,
      price: product.price,
      cost: product.cost,
      group_ids: product.group_ids,
      image_url: product.image_url || '',
      description: product.description || '',
      supplier_barcode: product.supplier_barcode || '',
      supplier_id: product.supplier_id || null,
      manualPrices: {},
      b2b_backorder_override: product.b2b_backorder_override,
      is_active: product.is_active,
      gallery_images: [],
    });
    setProductType('regular');
    setFormError('');
    setSkuManuallyOverridden(true);
    setSuggestedSeqSku('');
    setSkuPrefixResolved('');
    setModalOpen(true);
    loadProductPricelists(product.id);

    const { data: imgs } = await supabase
      .from('inventory_product_images')
      .select('id, image_url, alt_text, position')
      .eq('product_id', product.id)
      .order('position');
    if (imgs) {
      setForm((prev) => ({ ...prev, gallery_images: imgs.map((r: any) => ({ id: r.id, image_url: r.image_url, alt_text: r.alt_text || '', position: r.position })) }));
    }
  };

  const setField = (key: keyof ProductForm, value: any) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const resolveSkuPrefix = async () => {
    if (!activeCompanyId) return;
    setSkuPrefixLoading(true);
    try {
      const { data, error: err } = await supabase
        .from('companies')
        .select('sku_prefix')
        .eq('id', activeCompanyId)
        .single();
      if (err) throw err;
      setSkuPrefixResolved(data?.sku_prefix || '');
    } catch (err) {
      console.error('Error resolving SKU prefix:', err);
    } finally {
      setSkuPrefixLoading(false);
    }
  };

  useEffect(() => {
    if (!skuManuallyOverridden && modalOpen && !skuPrefixResolved) {
      resolveSkuPrefix();
    }
  }, [modalOpen, skuManuallyOverridden, skuPrefixResolved]);

  const saveProduct = async () => {
    if (!form.name.trim() || !form.sku.trim()) {
      setFormError('שם ומקט הם חובה');
      return;
    }
    if (form.group_ids.length > MAX_GROUPS) {
      setFormError(`מקסימום ${MAX_GROUPS} קבוצות`);
      return;
    }

    let resolvedSku = form.sku.trim();
    let resolvedBarcode = form.supplier_barcode.trim();

    const supplierMatch = /^(\d+)\*$/.test(resolvedSku);
    if (supplierMatch && form.supplier_id) {
      const supplier = suppliers.find((s) => s.id === form.supplier_id);
      if (supplier) {
        resolvedSku = supplier.supplier_number + '*' + resolvedSku.substring(0, resolvedSku.length - 1);
      }
    }

    setSaving(true);
    try {
      const productPayload = {
        sku: resolvedSku,
        name: form.name.trim(),
        price: form.price,
        cost: form.cost,
        image_url: form.image_url.trim() || null,
        description: form.description.trim() || null,
        supplier_barcode: resolvedBarcode || null,
        supplier_id: form.supplier_id || null,
        b2b_backorder_override: form.b2b_backorder_override,
        is_active: form.is_active,
      };

      let productId: string;

      if (editing) {
        productId = editing.id;
        const { error: err } = await supabase
          .from('inventory_products')
          .update(productPayload)
          .eq('id', editing.id);
        if (err) throw err;

        await supabase
          .from('inventory_product_group_relations')
          .delete()
          .eq('product_id', editing.id);

        if (form.group_ids.length > 0) {
          const relations = form.group_ids.map((gid) => ({
            product_id: editing.id,
            group_id: gid,
          }));
          const { error: relErr } = await supabase
            .from('inventory_product_group_relations')
            .insert(relations);
          if (relErr) throw relErr;
        }
      } else {
        const { data: newProduct, error: err } = await supabase
          .from('inventory_products')
          .insert([{ ...productPayload, company_id: activeCompanyId }])
          .select()
          .single();
        if (err) throw err;
        productId = newProduct.id;

        if (form.group_ids.length > 0) {
          const relations = form.group_ids.map((gid) => ({
            product_id: newProduct.id,
            group_id: gid,
          }));
          const { error: relErr } = await supabase
            .from('inventory_product_group_relations')
            .insert(relations);
          if (relErr) throw relErr;
        }
      }

      // Sync gallery images: delete all then re-insert in order
      await supabase
        .from('inventory_product_images')
        .delete()
        .eq('product_id', productId);

      if (form.gallery_images.length > 0) {
        const imgRows = form.gallery_images.map((img, idx) => ({
          product_id: productId,
          image_url: img.image_url,
          alt_text: img.alt_text || null,
          position: idx,
        }));
        const { error: imgErr } = await supabase
          .from('inventory_product_images')
          .insert(imgRows);
        if (imgErr) throw imgErr;
      }

      setToast({ msg: editing ? 'מוצר עודכן' : 'מוצר נוסף', ok: true });
      setModalOpen(false);
      fetchProducts();
    } catch (err: any) {
      setFormError(err.message || 'Error saving product');
      setToast({ msg: err.message || 'Error saving product', ok: false });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = async (product: InventoryProduct) => {
    setDeleteChecking(true);
    setDeleteTarget(product);
    try {
      const { count } = await supabase
        .from('inventory_document_lines')
        .select('id', { count: 'exact', head: true })
        .eq('product_id', product.id);
      setDeleteHasOperations((count ?? 0) > 0);
    } catch {
      setDeleteHasOperations(false);
    } finally {
      setDeleteChecking(false);
    }
  };

  const handleDeactivateFromDialog = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    const { error: err } = await supabase
      .from('inventory_products')
      .update({ is_active: false })
      .eq('id', deleteTarget.id);
    setSaving(false);
    if (err) {
      setToast({ msg: 'שגיאה בעדכון הסטטוס', ok: false });
    } else {
      setToast({ msg: `המוצר "${deleteTarget.name}" הוגדר כלא פעיל`, ok: true });
      fetchProducts();
    }
    setDeleteTarget(null);
  };

  const deleteProduct = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      if (deleteTarget.image_url) {
        await deleteStorageObject(pathFromUrl(deleteTarget.image_url));
      }

      const { error: err } = await supabase
        .from('inventory_products')
        .delete()
        .eq('id', deleteTarget.id);
      if (err) throw err;

      setToast({ msg: 'מוצר מחוק', ok: true });
      setDeleteTarget(null);
      fetchProducts();
    } catch (err: any) {
      const msg = err?.message || err?.code || '';
      const isFkViolation = msg.includes('23503') || msg.includes('violates foreign key') || msg.includes('referenced');
      setToast({
        msg: isFkViolation
          ? 'לא ניתן למחוק מוצר שבוצעו בו פעולות מלאי'
          : msg || 'שגיאה במחיקת המוצר',
        ok: false,
      });
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  if (!activeCompanyId) return <div className="p-4">Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Package className="w-6 h-6 text-amber-600" />
            <h1 className="text-2xl font-bold text-gray-900">מרכז ניהול מלאי</h1>
          </div>
          {activeTab === 'catalog' && (
            <div className="flex items-center gap-2">
              {!bulkEditMode && filtered.length > 0 && (
                <>
                  <button
                    onClick={async () => { setExporting(true); try { await exportProductsToExcel(filtered, supplierMap); } catch (e) { console.error('Products Excel export failed:', e); emitToast('שגיאה ביצירת קובץ אקסל', 'error'); } finally { setExporting(false); } }}
                    disabled={exporting}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg border border-emerald-200 bg-emerald-50 text-sm text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 transition-colors"
                  >
                    <FileSpreadsheet className="w-4 h-4" />
                    אקסל
                  </button>
                  <button
                    onClick={async () => { if (!catalogTableRef.current) return; setExporting(true); try { await exportProductsToPDF(catalogTableRef.current); } catch (e) { console.error('Products PDF export failed:', e); emitToast('שגיאה ביצירת PDF', 'error'); } finally { setExporting(false); } }}
                    disabled={exporting}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg border border-blue-200 bg-blue-50 text-sm text-blue-700 hover:bg-blue-100 disabled:opacity-50 transition-colors"
                  >
                    <FileDown className="w-4 h-4" />
                    PDF
                  </button>
                </>
              )}
              {canEdit && (
                <>
                  <button
                    onClick={() => setBulkEditMode(!bulkEditMode)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      bulkEditMode
                        ? 'bg-amber-100 text-amber-800 border border-amber-300'
                        : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                    }`}
                  >
                    <Table2 className="w-4 h-4" />
                    {bulkEditMode ? 'חזרה לתצוגה רגילה' : 'עריכה קבוצתית'}
                  </button>
                  {!bulkEditMode && (
                    <button
                      onClick={openAddModal}
                      className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
                    >
                      <Plus className="w-4 h-4" />
                      הוסף מוצר
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Tab Bar */}
      <div className="bg-white border-b border-gray-200 px-6">
        <div className="flex gap-1">
          {HUB_TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 transition ${
                activeTab === tab.id
                  ? 'border-amber-600 text-amber-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-4 right-4 px-4 py-3 rounded-lg text-white z-50 flex items-center gap-2 ${
            toast.ok ? 'bg-green-500' : 'bg-red-500'
          }`}
        >
          {toast.ok ? (
            <CheckCircle2 className="w-4 h-4" />
          ) : (
            <AlertCircle className="w-4 h-4" />
          )}
          {toast.msg}
        </div>
      )}

      {/* Shopify Archived Alert */}
      {showArchivedAlert && (
        <div className="bg-yellow-50 border-b border-yellow-200 px-6 py-3 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
          <div className="flex-1">
            <div className="text-sm font-medium text-yellow-800">
              {archivedCount} מוצרים מארכיון בשופיפיי
            </div>
          </div>
          <button
            onClick={() => setShopifyFilter('archived')}
            className="text-sm text-yellow-700 underline hover:text-yellow-900"
          >
            הצג
          </button>
          <button
            onClick={() => setShowArchivedAlert(false)}
            className="text-yellow-600 hover:text-yellow-700"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Tab Content */}
      <div className="bg-white">
        {activeTab === 'balances' && <InventoryBalancesView companyId={activeCompanyId} />}
        {activeTab === 'external_balances' && <ExternalWarehouseBalancesView companyId={activeCompanyId} />}
        {activeTab === 'movements' && <InventoryMovementsView companyId={activeCompanyId} />}

        {activeTab === 'catalog' && (
          <div className="p-6">
            {/* Toolbar */}
            <div className="flex items-center gap-3 flex-wrap mb-6">
              {/* Search Input */}
              <div className="flex-1 min-w-64 relative">
                <Search className="absolute right-3 top-3 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="חיפוש לפי שם, מקט, ספק או קבוצה…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pr-10 pl-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>

              {/* Supplier Filter */}
              {suppliers.length > 0 && (
                <select
                  value={supplierFilter}
                  onChange={(e) => setSupplierFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  <option value="">כל הספקים</option>
                  {suppliers.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              )}

              {/* Shopify Archived Filter */}
              {showShopifyColumns && (
                <button
                  onClick={() => setShopifyFilter(shopifyFilter === 'archived' ? null : 'archived')}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition ${
                    shopifyFilter === 'archived'
                      ? 'bg-amber-100 text-amber-800 border border-amber-300'
                      : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                  }`}
                >
                  <ArchiveX className="w-4 h-4 inline mr-1" />
                  מארכיון
                </button>
              )}

              {/* Show Inactive Products */}
              <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                />
                הצג גם לא פעילים
              </label>
            </div>

            {/* Loading / Error */}
            {loading && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 text-amber-600 animate-spin" />
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
                {error}
              </div>
            )}

            {/* Bulk Edit View */}
            {!loading && bulkEditMode && (
              <BulkEditProductsView
                products={filtered}
                suppliers={suppliers}
                groups={groups}
                onSaved={fetchProducts}
              />
            )}

            {/* Table */}
            {!loading && !bulkEditMode && (
              <div ref={catalogTableRef} className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-right font-semibold text-gray-700">תמונה</th>
                      <SortableTh label="מקט" sortKey="sku" activeKey={sort.key} dir={sort.dir} onSort={toggle} className="font-semibold text-gray-700" />
                      <SortableTh label="שם הפריט" sortKey="name" activeKey={sort.key} dir={sort.dir} onSort={toggle} className="font-semibold text-gray-700" />
                      <SortableTh label="קבוצה" sortKey="group_names" activeKey={sort.key} dir={sort.dir} onSort={toggle} className="font-semibold text-gray-700" />
                      <SortableTh label="ספק" sortKey="supplier_name" activeKey={sort.key} dir={sort.dir} onSort={toggle} className="font-semibold text-gray-700" />
                      <SortableTh label="מחיר צרכן" sortKey="price" activeKey={sort.key} dir={sort.dir} onSort={toggle} className="font-semibold text-gray-700" />
                      <SortableTh label="עלות ספק" sortKey="cost" activeKey={sort.key} dir={sort.dir} onSort={toggle} className="font-semibold text-gray-700" />
                      {showShopifyColumns && (
                        <th className="px-4 py-3 text-right font-semibold text-gray-700">שופיפיי</th>
                      )}
                      {canEdit && (
                        <th className="px-4 py-3 text-right font-semibold text-gray-700">פעולות</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 ? (
                      <tr>
                        <td colSpan={canEdit ? 9 : 8} className="px-4 py-8 text-center text-gray-500">
                          אין תוצאות
                        </td>
                      </tr>
                    ) : (
                      filtered.map((product) => (
                        <tr key={product.id} className={`border-b border-gray-200 hover:bg-gray-50 ${!product.is_active ? 'opacity-50 bg-gray-50' : ''}`}>
                          {/* Thumbnail */}
                          <td className="px-4 py-3">
                            {product.image_url ? (
                              <img
                                src={product.image_url}
                                alt={product.name}
                                className="w-10 h-10 object-cover rounded"
                              />
                            ) : (
                              <div className="w-10 h-10 bg-gray-200 rounded flex items-center justify-center">
                                <ImageIcon className="w-5 h-5 text-gray-400" />
                              </div>
                            )}
                          </td>

                          {/* SKU */}
                          <td className="px-4 py-3 font-mono font-semibold text-gray-900">
                            {product.sku}
                          </td>

                          {/* Name */}
                          <td className="px-4 py-3 text-gray-900">
                            {product.name}
                            {!product.is_active && (
                              <span className="mr-2 inline-block text-[10px] font-medium bg-red-100 text-red-600 px-1.5 py-0.5 rounded">
                                לא פעיל
                              </span>
                            )}
                          </td>

                          {/* Groups */}
                          <td className="px-4 py-3">
                            {product.group_names.length > 0 ? (
                              <div className="flex gap-1 flex-wrap">
                                {product.group_names.map((gn, i) => (
                                  <span
                                    key={i}
                                    className="inline-block px-2 py-1 bg-amber-100 text-amber-800 text-xs rounded"
                                  >
                                    {gn}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-gray-400 text-xs">—</span>
                            )}
                          </td>

                          {/* Supplier */}
                          <td className="px-4 py-3">
                            {product.supplier_id ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                                <Truck className="w-3 h-3" />
                                {supplierMap[product.supplier_id] || 'Unknown'}
                              </span>
                            ) : (
                              <span className="text-gray-400 text-xs">—</span>
                            )}
                          </td>

                          {/* Price */}
                          <td className="px-4 py-3 text-gray-900">{fmt(product.price)} ₪</td>

                          {/* Cost */}
                          <td className="px-4 py-3 text-gray-900">
                            {product.cost !== null ? fmt(product.cost) + ' ₪' : '—'}
                          </td>

                          {/* Shopify Status */}
                          {showShopifyColumns && (
                            <td className="px-4 py-3">
                              {product.shopify_status && (
                                <span
                                  className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded ${
                                    product.shopify_status === 'archived'
                                      ? 'bg-red-100 text-red-800'
                                      : product.shopify_status === 'active'
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-gray-100 text-gray-800'
                                  }`}
                                >
                                  {product.shopify_status === 'archived' && (
                                    <ArchiveX className="w-3 h-3" />
                                  )}
                                  {product.shopify_status === 'active' && (
                                    <PackageCheck className="w-3 h-3" />
                                  )}
                                  {product.shopify_status === 'draft' && (
                                    <PackageX className="w-3 h-3" />
                                  )}
                                  {product.shopify_status}
                                </span>
                              )}
                            </td>
                          )}

                          {/* Actions */}
                          {canEdit && (
                            <td className="px-4 py-3 flex gap-2">
                              <button
                                onClick={() => openEditModal(product)}
                                className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteClick(product)}
                                className="p-1 text-red-600 hover:bg-red-50 rounded"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Footer Count */}
            {!loading && (
              <div className="mt-4 text-sm text-gray-600">
                {filtered.length} מוצרים (מתוך {products.length} בסה"כ)
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 backdrop-blur-sm overflow-y-auto pt-20 pb-8 px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-auto">
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">
                {editing ? 'עריכת מוצר' : 'הוספת מוצר'}
              </h2>
              <button
                onClick={() => setModalOpen(false)}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-5">
              {formError && (
                <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                  {formError}
                </div>
              )}

              {/* Product Type Selection (Add only) */}
              {!editing && (
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700">סוג המוצר</label>
                  <div className="flex gap-3">
                    <label className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 cursor-pointer text-sm transition-colors ${productType === 'regular' ? 'border-amber-500 bg-amber-50 text-amber-700 font-medium' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                      <input
                        type="radio"
                        className="hidden"
                        checked={productType === 'regular'}
                        onChange={() => setProductType('regular')}
                      />
                      מוצר רגיל
                    </label>
                    <label className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 cursor-pointer text-sm transition-colors ${productType === 'variation' ? 'border-amber-500 bg-amber-50 text-amber-700 font-medium' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                      <input
                        type="radio"
                        className="hidden"
                        checked={productType === 'variation'}
                        onChange={() => setProductType('variation')}
                      />
                      בנייה עם וריאציות
                    </label>
                  </div>
                </div>
              )}

              {productType === 'variation' && !editing && (
                <button
                  onClick={() => setWizardOpen(true)}
                  className="w-full px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 text-sm font-medium transition-colors"
                >
                  <Layers className="w-4 h-4 inline mr-2" />
                  פתח אשף וריאציות
                </button>
              )}

              {/* Basic Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-sm font-semibold text-gray-700">מקט</label>
                  {!skuManuallyOverridden && skuPrefixLoading ? (
                    <div className="px-3 py-2.5 border border-gray-200 rounded-lg bg-gray-50 flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-amber-500" />
                      <span className="text-sm text-gray-500">טוען...</span>
                    </div>
                  ) : (
                    <>
                      <input
                        type="text"
                        value={form.sku}
                        onChange={(e) => {
                          setField('sku', e.target.value);
                          if (e.target.value !== '') setSkuManuallyOverridden(true);
                        }}
                        placeholder={skuPrefixResolved || 'מקט'}
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-shadow"
                      />
                      {!skuManuallyOverridden && !editing && (
                        <p className="text-xs text-gray-400 mt-1">קידומת: {skuPrefixResolved}</p>
                      )}
                    </>
                  )}
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-semibold text-gray-700">שם הפריט</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setField('name', e.target.value)}
                    placeholder="שם המוצר"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-shadow"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-semibold text-gray-700">מחיר צרכן</label>
                  <input
                    type="number"
                    value={form.price}
                    onChange={(e) => setField('price', parseFloat(e.target.value) || 0)}
                    step="0.01"
                    min="0"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-shadow"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-semibold text-gray-700">עלות ספק</label>
                  <input
                    type="number"
                    value={form.cost ?? ''}
                    onChange={(e) => setField('cost', e.target.value ? parseFloat(e.target.value) : null)}
                    step="0.01"
                    min="0"
                    placeholder="(optional)"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-shadow"
                  />
                </div>
              </div>

              {/* Smart SKU Builder (Add only) */}
              {!editing && !skuManuallyOverridden && (
                <SmartSkuBuilder
                  prefix={skuPrefixResolved}
                  onSeqSku={(sku) => setSuggestedSeqSku(sku)}
                  vatRate={vatRate}
                  priceInput={form.price}
                  costInput={form.cost}
                  suppliers={suppliers}
                />
              )}

              {/* Image Upload */}
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-gray-700">תמונה ראשית (URL)</label>
                <input
                  type="text"
                  value={form.image_url}
                  onChange={(e) => setField('image_url', e.target.value)}
                  placeholder="https://..."
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-shadow"
                />
                {form.image_url && (
                  <img
                    src={form.image_url}
                    alt="preview"
                    className="w-20 h-20 object-cover rounded-lg mt-2 border border-gray-100"
                  />
                )}
              </div>

              {/* Gallery Images */}
              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700">גלריית תמונות ({form.gallery_images.length})</label>
                {form.gallery_images.length > 0 && (
                  <div className="space-y-2">
                    {form.gallery_images.map((img, idx) => (
                      <div key={img.id || idx} className="flex items-center gap-2 bg-gray-50 p-2 rounded-lg border border-gray-200">
                        <img src={img.image_url} alt={img.alt_text || ''} className="w-12 h-12 object-cover rounded flex-shrink-0" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                        <input
                          type="text"
                          value={img.alt_text}
                          onChange={(e) => {
                            const updated = [...form.gallery_images];
                            updated[idx] = { ...updated[idx]!, alt_text: e.target.value };
                            setField('gallery_images', updated);
                          }}
                          placeholder="טקסט חלופי"
                          className="flex-1 min-w-0 px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                        />
                        <div className="flex flex-col gap-0.5">
                          <button
                            type="button"
                            disabled={idx === 0}
                            onClick={() => {
                              const updated = [...form.gallery_images];
                              [updated[idx - 1], updated[idx]] = [updated[idx]!, updated[idx - 1]!];
                              setField('gallery_images', updated);
                            }}
                            className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                          >
                            <ChevronUp className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            disabled={idx === form.gallery_images.length - 1}
                            onClick={() => {
                              const updated = [...form.gallery_images];
                              [updated[idx], updated[idx + 1]] = [updated[idx + 1]!, updated[idx]!];
                              setField('gallery_images', updated);
                            }}
                            className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                          >
                            <ChevronDown className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setField('gallery_images', form.gallery_images.filter((_, i) => i !== idx));
                          }}
                          className="p-1 text-red-500 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <input
                    type="text"
                    id="new-gallery-url"
                    placeholder="הוסף כתובת תמונה חדשה..."
                    className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const input = e.target as HTMLInputElement;
                        const url = input.value.trim();
                        if (url) {
                          setField('gallery_images', [...form.gallery_images, { id: crypto.randomUUID(), image_url: url, alt_text: '', position: form.gallery_images.length }]);
                          input.value = '';
                        }
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const input = document.getElementById('new-gallery-url') as HTMLInputElement;
                      const url = input?.value.trim();
                      if (url) {
                        setField('gallery_images', [...form.gallery_images, { id: crypto.randomUUID(), image_url: url, alt_text: '', position: form.gallery_images.length }]);
                        input.value = '';
                      }
                    }}
                    className="px-3 py-2 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 text-sm font-medium transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Description */}
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-gray-700">תיאור מוצר</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setField('description', e.target.value)}
                  placeholder="תיאור המוצר (תומך ב-HTML מ-Shopify)..."
                  rows={4}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-shadow resize-y"
                />
              </div>

              {/* Supplier */}
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-gray-700">ספק</label>
                <SearchableSelect
                  options={suppliers.map(s => ({ value: s.id, label: `#${s.supplier_number} ${s.name}` }))}
                  value={form.supplier_id ?? ''}
                  onChange={v => setField('supplier_id', v || null)}
                  placeholder="— בחר ספק —"
                />
                {suppliers.length === 0 && (
                  <p className="text-xs text-gray-400">
                    אין ספקים עדיין — הוסף ספקים בהגדרות מלאי
                  </p>
                )}
              </div>

              {/* Supplier Barcode */}
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <Barcode className="w-4 h-4" />
                  ברקוד ספק
                </label>
                <input
                  type="text"
                  value={form.supplier_barcode}
                  onChange={(e) => setField('supplier_barcode', e.target.value)}
                  placeholder="ברקוד"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-shadow"
                />
              </div>

              {/* Groups */}
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <Folder className="w-4 h-4" />
                  קבוצות ({form.group_ids.length}/{MAX_GROUPS})
                </label>
                <div className="space-y-1 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-2.5 bg-gray-50 scrollbar-thin">
                  {groupOptions.map((g) => (
                    <label key={g.id} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900 py-0.5">
                      <input
                        type="checkbox"
                        className="rounded"
                        checked={form.group_ids.includes(g.id)}
                        onChange={(e) => {
                          if (e.target.checked && form.group_ids.length >= MAX_GROUPS) return;
                          setField(
                            'group_ids',
                            e.target.checked
                              ? [...form.group_ids, g.id]
                              : form.group_ids.filter((id) => id !== g.id)
                          );
                        }}
                      />
                      {g.name}
                    </label>
                  ))}
                </div>
              </div>

              {/* Active / Inactive Toggle */}
              <div className="flex items-center gap-3">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => setField('is_active', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-amber-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500"></div>
                </label>
                <span className="text-sm font-semibold text-gray-700">
                  {form.is_active ? 'מוצר פעיל' : 'מוצר לא פעיל'}
                </span>
              </div>

              {/* B2B Backorder Override */}
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-gray-700">
                  מדיניות B2B — הזמנה מאפס מלאי
                </label>
                <select
                  value={
                    form.b2b_backorder_override === null
                      ? 'inherit'
                      : form.b2b_backorder_override
                        ? 'allow'
                        : 'deny'
                  }
                  onChange={(e) => {
                    if (e.target.value === 'inherit') {
                      setField('b2b_backorder_override', null);
                    } else if (e.target.value === 'allow') {
                      setField('b2b_backorder_override', true);
                    } else {
                      setField('b2b_backorder_override', false);
                    }
                  }}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-shadow bg-white"
                >
                  <option value="inherit">ירש מהגדרת החברה הכללית</option>
                  <option value="allow">תמיד אפשר — גם ללא מלאי זמין</option>
                  <option value="deny">אף פעם לא — חסום כשאין מלאי</option>
                </select>
              </div>

              {/* Manual Pricelists */}
              <div className="space-y-2">
                <button
                  onClick={() => setMlExpanded(!mlExpanded)}
                  className="flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-gray-900 transition-colors"
                >
                  <ChevronDown className={`w-4 h-4 transition-transform ${mlExpanded ? 'rotate-180' : ''}`} />
                  חוזי מחירים מנוהלים
                </button>

                {mlExpanded && (
                  <div className="space-y-3 bg-gray-50 p-3.5 rounded-xl border border-gray-100">
                    {mlLoading ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="w-4 h-4 text-amber-500 animate-spin" />
                      </div>
                    ) : formPricelists.filter((p) => p.is_manual).length === 0 ? (
                      <p className="text-xs text-gray-400">אין חוזי מחירים מנוהלים</p>
                    ) : (
                      formPricelists
                        .filter((p) => p.is_manual)
                        .map((pl) => (
                          <div key={pl.id} className="space-y-1">
                            <label className="text-xs font-semibold text-gray-600">{pl.name}</label>
                            <input
                              type="number"
                              value={form.manualPrices[pl.id] || ''}
                              onChange={(e) => {
                                setField('manualPrices', {
                                  ...form.manualPrices,
                                  [pl.id]: e.target.value,
                                });
                              }}
                              step="0.01"
                              min="0"
                              placeholder="0.00"
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                            />
                          </div>
                        ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Modal Actions */}
            <div className="border-t border-gray-100 px-6 py-4 flex gap-3 justify-end">
              <button
                onClick={() => setModalOpen(false)}
                className="px-4 py-2.5 border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 text-sm font-medium transition-colors"
              >
                ביטול
              </button>
              <button
                onClick={saveProduct}
                disabled={saving}
                className="px-5 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4" />
                )}
                {editing ? 'עדכן' : 'הוסף'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteTarget && !deleteChecking && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-auto">
            {deleteHasOperations ? (
              <>
                <div className="px-6 py-5 space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-50 rounded-xl">
                      <AlertCircle className="w-5 h-5 text-amber-600" />
                    </div>
                    <h2 className="text-lg font-bold text-gray-900">לא ניתן למחוק מוצר</h2>
                  </div>
                  <p className="text-sm text-gray-600">
                    לא ניתן למחוק את המוצר <strong>"{deleteTarget.name}"</strong> מכיוון שבוצעו בו פעולות מלאי.
                  </p>
                  <p className="text-sm text-gray-500">
                    ניתן להגדיר את המוצר כ<strong>לא פעיל</strong> — המוצר לא יוצג בקטלוג אך ימשיך להופיע בדוחות.
                  </p>
                </div>
                <div className="border-t border-gray-100 px-6 py-4 flex gap-3 justify-end">
                  <button
                    onClick={() => setDeleteTarget(null)}
                    className="px-4 py-2.5 border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 text-sm font-medium transition-colors"
                  >
                    ביטול
                  </button>
                  <button
                    onClick={handleDeactivateFromDialog}
                    disabled={saving}
                    className="px-4 py-2.5 bg-amber-500 text-white rounded-xl hover:bg-amber-600 disabled:opacity-50 text-sm font-medium transition-colors flex items-center gap-2"
                  >
                    {saving ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <PackageX className="w-4 h-4" />
                    )}
                    סמן כלא פעיל
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="px-6 py-5 space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-50 rounded-xl">
                      <AlertCircle className="w-5 h-5 text-red-600" />
                    </div>
                    <h2 className="text-lg font-bold text-gray-900">מחיקת מוצר</h2>
                  </div>
                  <p className="text-sm text-gray-600">
                    האם אתה בטוח שברצונך למחוק את המוצר "{deleteTarget.name}"? לא ניתן לבטל פעולה זו.
                  </p>
                </div>
                <div className="border-t border-gray-100 px-6 py-4 flex gap-3 justify-end">
                  <button
                    onClick={() => setDeleteTarget(null)}
                    className="px-4 py-2.5 border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 text-sm font-medium transition-colors"
                  >
                    ביטול
                  </button>
                  <button
                    onClick={deleteProduct}
                    disabled={deleting}
                    className="px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 disabled:opacity-50 text-sm font-medium transition-colors flex items-center gap-2"
                  >
                    {deleting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                    מחק
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Variation Wizard Modal */}
      {wizardOpen && (
        <VariationWizard
          companyId={activeCompanyId ?? ''}
          groupOptions={groupOptions.map(g => ({ id: g.id, label: g.name }))}
          skuSettings={skuSettings}
          onClose={() => setWizardOpen(false)}
          onCreated={() => {
            setWizardOpen(false);
            setModalOpen(false);
            fetchProducts();
          }}
        />
      )}
    </div>
  );
}
